// My Executive Assistant — §9's second section.
//
// "**links into Customize EA** (does not duplicate it)".
//
// That parenthesis still holds: the 11-question setup is NOT re-implemented
// here, because a second place to set an assistant's personality is exactly
// the duplication §9 forbids.
//
// WHAT CHANGED, AND WHY THE OLD NOTE WAS WRONG (#331, 2026-10-24)
// ===============================================================
// This page said the Personal EA runtime was "off in this environment" and
// that nothing here needed to change when the flag flipped. Both halves were
// wrong by the time Nick read them:
//
//   * `CIPHER_PERSONAL_EA_V1` has been `true` on the backend since
//     2026-09-01. The note described a deployment that no longer existed,
//     which is worse than saying nothing — he trusted it and concluded the
//     API was failing.
//   * Something DID need to change: there was no way to switch an assistant
//     on. `POST /api/me/ea/activate` has existed the whole time and no
//     screen called it, so a finished questionnaire led nowhere.
//
// The on/off control is here rather than on the assistant's own page on
// purpose: switching her on is an account setting, her own page is where you
// talk to her, and this is the page Nick went looking on.
//
// THIS SCREEN NEVER DECIDES WHETHER SHE IS ON. It asks the server and shows
// the answer. Stating a runtime fact from a constant is the mistake being
// corrected here, and repeating it would be worse the second time.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Block, NotBuilt, Row, Value } from "../ui";
import { get, post, ENDPOINTS } from "../../ea/api";

// The 11-question onboarding flow, verbatim in order
// (`shell/routes/ea/questions.js` is the source of truth for the copy).
const COVERED = [
  ["Purpose", "What you mainly want your EA for."],
  ["Personality", "How it should come across."],
  ["Communication style", "How it talks to you."],
  ["Initiative", "How much it does on its own."],
  ["Interruption preferences", "When it should interrupt you."],
  ["Briefing format", "How briefings are laid out."],
  ["Follow-up persistence", "How persistent it is about open items."],
  ["Working hours", "When you're working — start, end, days, timezone."],
  ["Notification preferences", "Channel and frequency."],
  ["Priorities", "What it should keep an eye on for you."],
  ["Recurring responsibilities", "What it should handle on a schedule."],
];

function OnOff() {
  const [state, setState] = useState({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const res = await get(ENDPOINTS.myEA);
    if (res.status !== "ok") {
      setState({ status: res.status });
      return;
    }
    setState({ status: "ok", ea: res.data?.ea || null,
               missing: res.data?.onboarding_missing || [] });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function act(path) {
    setBusy(true);
    setError(null);
    const res = await post(path, {});
    setBusy(false);
    if (res.status !== "ok") {
      setError(res.error || "That did not work.");
      return;
    }
    await load();
  }

  if (state.status === "loading") {
    return (
      <Block title="Your assistant">
        <Row label="Status"><Value><span className="dim">Checking…</span></Value></Row>
      </Block>
    );
  }

  // The subsystem is off for this deployment — said plainly, and sourced
  // from the server rather than asserted by this file.
  if (state.status === "disabled") {
    return (
      <Block title="Your assistant">
        <NotBuilt
          what="The assistant is not switched on for this deployment."
          why={"Cipher's assistant runtime is disabled here, so there is nothing "
            + "to switch on yet. This is read from the server, not assumed."}
        />
      </Block>
    );
  }
  if (state.status !== "ok") {
    return (
      <Block title="Your assistant">
        <NotBuilt
          what="Your assistant's status could not be read."
          why="Sign in again and reopen this page."
        />
      </Block>
    );
  }

  const ea = state.ea || {};
  const live = ea.lifecycle === "live";
  const complete = !!ea.onboarding_complete;
  const missing = state.missing || [];

  return (
    <Block
      title="Your assistant"
      note="Switching on is yours alone. Nobody else can do it for you, and nothing does it automatically."
      actions={complete ? (
        <button
          type="button"
          className="btn-ghost btn-sm"
          disabled={busy}
          onClick={() => act(live ? ENDPOINTS.suspend : ENDPOINTS.activate)}
        >
          {busy ? "Working…" : live ? "Switch off" : "Switch on"}
        </button>
      ) : null}
    >
      <Row
        label="Status"
        hint={live
          ? "She can hold a conversation and will reply."
          : "She is set up but not running. Nothing reaches her."}
      >
        <Value>{live ? "On" : "Off — dormant"}</Value>
      </Row>
      <Row
        label="Setup"
        hint={complete
          ? "All 11 questions answered."
          : "Finish setup before she can be switched on."}
      >
        <Value>
          {complete ? "Complete" : (
            <>
              <span className="dim">
                {missing.length} question{missing.length === 1 ? "" : "s"} left
              </span>{" "}
              <Link className="btn-ghost btn-sm" to="/v2/ea">Finish setup</Link>
            </>
          )}
        </Value>
      </Row>
      {error ? (
        <Row label="">
          <Value><span className="dim">{error}</span></Value>
        </Row>
      ) : null}
    </Block>
  );
}

export default function MyEA() {
  return (
    <>
      <OnOff />

      <Block
        title="Customize EA"
        note="Your assistant is configured in one place. This is the way in, not a second copy of it."
        actions={
          <Link className="btn-ghost btn-sm" to="/v2/ea">
            Open Executive Assistant
          </Link>
        }
      >
        <Row
          label="Where it is set"
          hint="The 11-question setup lives on the assistant's own page."
        >
          <Value><span className="dim">Open Executive Assistant</span></Value>
        </Row>
      </Block>

      <Block
        title="What Customize EA covers"
        note="The 11 onboarding questions, in order. None of it is set here — open the link above."
      >
        {COVERED.map(([label, hint]) => (
          <Row key={label} label={label} hint={hint}>
            <Value>
              <span className="dim">Set in Customize EA</span>
            </Value>
          </Row>
        ))}
      </Block>
    </>
  );
}
